import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { lookupUPC } from '../api';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function BarcodeScanner({ onResult, onClose }) {
  const [status, setStatus] = useState('starting'); // starting | scanning | looking-up | error
  const [error, setError] = useState(null);
  const [scannedCode, setScannedCode] = useState(null);
  const scannerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let html5Qrcode = null;
    let stopped = false;

    async function startScanner() {
      try {
        html5Qrcode = new Html5Qrcode('barcode-reader');
        scannerRef.current = html5Qrcode;

        await html5Qrcode.start(
          { facingMode: 'environment' },
          {
            fps: 15,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const w = Math.min(viewfinderWidth * 0.85, 500);
              const h = Math.min(viewfinderHeight * 0.4, 200);
              return { width: Math.floor(w), height: Math.floor(h) };
            },
            formatsToSupport: [
              Html5QrcodeSupportedFormats.EAN_13,
              Html5QrcodeSupportedFormats.EAN_8,
              Html5QrcodeSupportedFormats.UPC_A,
              Html5QrcodeSupportedFormats.UPC_E,
              Html5QrcodeSupportedFormats.UPC_EAN_EXTENSION,
              Html5QrcodeSupportedFormats.CODE_128,
              Html5QrcodeSupportedFormats.CODE_39,
              Html5QrcodeSupportedFormats.ITF,
              Html5QrcodeSupportedFormats.QR_CODE,
            ]
          },
          async (decodedText) => {
            if (stopped) return;
            stopped = true;

            // Stop camera in background — don't await it before showing status
            const stopPromise = html5Qrcode.stop().catch(() => {});

            setStatus('looking-up');
            setScannedCode(decodedText);

            // Wait for camera to actually stop so UI clears
            await stopPromise;

            try {
              const result = await lookupUPC(decodedText);
              onResult(result);
            } catch (e) {
              setError('Lookup failed. Please try again.');
              setStatus('error');
            }
          },
          () => {} // ignore scan failures (no match yet)
        );

        if (!stopped) setStatus('scanning');
      } catch (err) {
        if (!stopped) {
          setError('Could not access camera. Please allow camera permissions.');
          setStatus('error');
        }
      }
    }

    startScanner();

    return () => {
      stopped = true;
      if (scannerRef.current) {
        try {
          scannerRef.current.stop().catch(() => {});
        } catch (e) { /* ignore */ }
      }
    };
  }, [onResult]);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex flex-col items-center justify-center">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 bg-gray-800/80 hover:bg-gray-700 text-white p-3 rounded-full"
        style={{ minWidth: 48, minHeight: 48 }}
      >
        <XMarkIcon className="w-7 h-7" />
      </button>

      {/* Status text */}
      <div className="text-white text-lg font-medium mb-4">
        {status === 'starting' && 'Starting camera...'}
        {status === 'scanning' && 'Point at a barcode'}
        {status === 'looking-up' && 'Looking up product...'}
        {status === 'error' && (error || 'An error occurred')}
      </div>

      {/* Scanner viewfinder — hidden once scan succeeds */}
      {status !== 'looking-up' && (
        <div
          ref={containerRef}
          className="relative w-full max-w-lg mx-4 rounded-2xl overflow-hidden border-2 border-gray-600"
        >
          <div id="barcode-reader" style={{ width: '100%' }} />
        </div>
      )}

      {status === 'looking-up' && (
        <div className="mt-6 flex flex-col items-center gap-3">
          <svg className="animate-spin h-8 w-8 text-red-500" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-white text-lg font-medium">Looking up product...</span>
          {scannedCode && <span className="text-gray-400 text-sm font-mono">{scannedCode}</span>}
          <span className="text-gray-500 text-xs mt-1">This may take a few seconds</span>
        </div>
      )}

      {status === 'error' && (
        <button
          onClick={onClose}
          className="mt-6 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-medium"
        >
          Close
        </button>
      )}
    </div>
  );
}
